import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getVideo, listVideos, listComments, addComment, toggleLike, toggleSubscribe, toggleWatchLater, recordView, resolveFileUrl, getDownloadUrl } from '../lib/api';
import { useApp } from '../context/AppContext';
import VideoPlayer from '../components/VideoPlayer';
import PrerollAd from '../components/PrerollAd';
import ShareDialog from '../components/ShareDialog';
import { formatCount } from '../mock';
import { ThumbsUp, ThumbsDown, Share2, Download, Bookmark, Bell, CheckCircle2, Send, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export default function Watch() {
  const { id } = useParams();
  const { user } = useApp();
  const [video, setVideo] = useState(null);
  const [related, setRelated] = useState([]);
  const [comments, setComments] = useState([]);
  const [liked, setLiked] = useState(false);
  const [saved, setSaved] = useState(false);
  const [subbed, setSubbed] = useState(false);
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(true);
  const [adDone, setAdDone] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const v = await getVideo(id);
        if (cancelled) return;
        setVideo(v); setLiked(!!v.liked); setSaved(!!v.saved);
        // Related
        const rel = await listVideos({ category: v.category, limit: 12 });
        setRelated(rel.filter(x => x.id !== v.id).slice(0, 10));
        const c = await listComments(id);
        setComments(c);
        // fire view
        try { await recordView(id); } catch {}
        // subscription state
        if (user && v.channel) {
          try {
            const subs = (await (await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/me/subscriptions`, { headers: { Authorization: 'Bearer ' + localStorage.getItem('vidhub_token') } })).json());
            setSubbed(Array.isArray(subs) && subs.some(s => s.id === v.channel.id));
          } catch {}
        }
      } catch { toast.error('Failed to load video'); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [id, user]);

  // reset ad when switching videos
  useEffect(() => { setAdDone(false); }, [id]);

  if (loading) return <div className="flex items-center gap-2 text-white/60 py-20"><Loader2 className="w-5 h-5 animate-spin" /> Loading…</div>;
  if (!video) return <div className="text-white/60 py-10">Video not found.</div>;

  const channel = video.channel;

  const onLike = async () => {
    try { const { liked: l } = await toggleLike(video.id); setLiked(l); setVideo({ ...video, likes_count: video.likes_count + (l ? 1 : -1) }); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Sign in to like'); }
  };
  const onSave = async () => {
    try { const { saved: s } = await toggleWatchLater(video.id); setSaved(s); toast.success(s ? 'Saved' : 'Removed'); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Error'); }
  };
  const onSub = async () => {
    if (!channel) return;
    try { const { subscribed } = await toggleSubscribe(channel.id); setSubbed(subscribed); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Error'); }
  };
  const share = () => setShareOpen(true);
  const postComment = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    try { const c = await addComment(video.id, text.trim()); setComments([c, ...comments]); setText(''); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Sign in to comment'); }
  };

  return (
    <div className="grid xl:grid-cols-[1fr_380px] gap-8">
      <div>
        {adDone ? (
          <VideoPlayer source={video.source} poster={video.thumb} title={video.title} />
        ) : (
          <PrerollAd category={video.category} videoId={video.id} onDone={() => setAdDone(true)} />
        )}

        <h1 className="font-display text-[26px] md:text-[32px] mt-4 leading-tight">{video.title}</h1>

        <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            {channel && (
              <Link to={`/channel/${channel.id}`} className="flex items-center gap-3">
                <img src={resolveFileUrl(channel.avatar)} alt={channel.name} className="w-10 h-10 rounded-full object-cover" />
                <div>
                  <div className="flex items-center gap-1 text-[14.5px] font-semibold">{channel.name} {channel.verified && <CheckCircle2 className="w-4 h-4 text-white/50" />}</div>
                  <div className="text-[12px] text-white/55">{formatCount(channel.subs_count)} subscribers</div>
                </div>
              </Link>
            )}
            {channel && (
              <button onClick={onSub} className={subbed ? 'btn-ghost' : 'btn-primary'}>
                <Bell className="w-4 h-4" /> {subbed ? 'Subscribed' : 'Subscribe'}
              </button>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <button onClick={onLike} className={`chip ${liked ? 'active' : ''}`}><ThumbsUp className="w-4 h-4" /> {formatCount(video.likes_count)}</button>
            <button className="chip"><ThumbsDown className="w-4 h-4" /></button>
            <button onClick={share} className="chip" data-testid="watch-share-btn"><Share2 className="w-4 h-4" /> Share</button>
            <button onClick={onSave} className={`chip ${saved ? 'active' : ''}`}><Bookmark className="w-4 h-4" /> {saved ? 'Saved' : 'Save'}</button>
            <button className="chip" onClick={async () => {
              try {
                const { url: durl, filename } = await getDownloadUrl(video.id);
                const a = document.createElement('a'); a.href = durl; a.download = filename || 'video'; a.target = '_blank'; a.rel = 'noopener'; document.body.appendChild(a); a.click(); a.remove();
                toast.success('Download started');
              } catch (err) {
                if (err?.response?.status === 402) {
                  toast.error('Downloads require VidCreForge+', { action: { label: 'Upgrade', onClick: () => { window.location.href = '/memberships'; } } });
                } else {
                  toast.error(err?.response?.data?.detail || 'Download not available');
                }
              }
            }} data-testid="watch-download-btn"><Download className="w-4 h-4" /> Download</button>
          </div>
        </div>

        <div className="card-shell p-4 mt-4">
          <div className="text-[13px] text-white/70 font-semibold">{formatCount(video.views)} views • {video.uploaded_at}</div>
          <p className="text-[14px] text-white/80 mt-2 leading-relaxed whitespace-pre-line">{video.desc}</p>
          {video.tags?.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2">
              {video.tags.map(t => <span key={t} className="chip">#{t}</span>)}
            </div>
          )}
        </div>

        <div className="mt-8">
          <h2 className="font-display text-[24px]">{comments.length} Comments</h2>
          <form onSubmit={postComment} className="mt-3 flex items-center gap-2">
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#ff2e63] to-[#22d3ee]" />
            <input value={text} onChange={e => setText(e.target.value)} placeholder="Add a comment…" className="flex-1 bg-transparent border-b border-white/15 focus:border-[#ff2e63] py-2 text-[14px] focus:outline-none placeholder:text-white/40" />
            <button disabled={!text.trim()} className="btn-primary disabled:opacity-50"><Send className="w-4 h-4" /> Post</button>
          </form>
          <div className="mt-6 space-y-5">
            {comments.map(c => (
              <div key={c.id} className="flex gap-3">
                <img src={c.avatar} alt={c.user} className="w-9 h-9 rounded-full object-cover" />
                <div className="flex-1">
                  <div className="text-[13px]"><span className="font-semibold">@{c.user}</span> <span className="text-white/50 ml-2">{new Date(c.at).toLocaleString()}</span></div>
                  <div className="text-[14px] mt-1 text-white/90">{c.text}</div>
                </div>
              </div>
            ))}
            {comments.length === 0 && <div className="text-white/50 text-[13px]">Be the first to comment.</div>}
          </div>
        </div>
      </div>

      <aside className="space-y-3">
        <h3 className="row-title text-white">Up next</h3>
        {related.map(v => (
          <Link key={v.id} to={`/watch/${v.id}`} className="flex gap-3 group">
            <div className="w-40 shrink-0 thumb" style={{ aspectRatio: '16/9' }}>
              <img src={resolveFileUrl(v.thumb)} alt={v.title} />
              <span className="dur">{v.type === 'live' ? 'LIVE' : v.duration}</span>
            </div>
            <div className="min-w-0">
              <div className="text-[13.5px] font-semibold line-clamp-2 group-hover:text-[#ff6b8a]">{v.title}</div>
              <div className="text-[12px] text-white/55 mt-1">{v.channel?.name}</div>
              <div className="text-[12px] text-white/50">{formatCount(v.views)} views • {v.uploaded_at}</div>
            </div>
          </Link>
        ))}
      </aside>
      <ShareDialog open={shareOpen} onClose={() => setShareOpen(false)} video={video} />
    </div>
  );
}
